import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Navigation.css';
import { useAuth } from '../../contexts/AuthContext';
import { useUserRole } from '../../contexts/UserRoleContext';

interface NavigationProps {
  onSectionChange: (section: string) => void;
}

export const Navigation: React.FC<NavigationProps> = ({ onSectionChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const { hasAnyRole, loading: roleLoading, userProfile } = useUserRole();

  const baseNavItems = [
    { section: 'overview', label: 'Home →', color: 'blue' },
    { section: 'transportation', label: 'Transportation →', color: 'light-blue' },
    { section: 'buildings', label: 'Buildings →', color: 'gold' },
    { section: 'other', label: 'Waste +', color: 'orange' },
    { section: 'people', label: 'People →', color: 'pink' },
    { section: 'ghg', label: 'GHG inventory →', color: 'teal' }
  ];

  // Only show the Policy Tool entry when user is developer or super_admin
  // Wait for role loading to complete before checking
  const showPolicyTool = user && !roleLoading && hasAnyRole(['developer', 'super_admin']);
  
  // Debug logging to help diagnose role issues
  useEffect(() => {
    if (user) {
      console.log('Navigation - Role check debug:', {
        userId: user.id,
        roleLoading,
        userProfile: userProfile?.role,
        hasAnyRole: hasAnyRole(['developer', 'super_admin']),
        showPolicyTool
      });
    }
  }, [user, roleLoading, userProfile, hasAnyRole, showPolicyTool]);
  
  const navItems = showPolicyTool
    ? [
        ...baseNavItems,
        { section: 'policy-tool', label: 'Policy Tool →', color: 'purple' }
      ]
    : baseNavItems;

  const handleNavClick = (section: string) => {
    onSectionChange(section);
    // Navigate to root for overview, otherwise to section path
    if (section === 'overview') {
      navigate('/');
    } else {
      navigate(`/${section}`);
    }
    // Close mobile menu when navigating
    setIsMobileMenuOpen(false);
    // Scroll to top when navigating to a new section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCurrentSection = () => {
    const path = location.pathname;
    if (path === '/') return 'overview';
    return path.slice(1);
  };

  const currentSection = getCurrentSection();

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobileMenuOpen) {
        const target = event.target as Element;
        if (!target.closest('.nav-bar')) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  return (
    <div className="nav-bar">
      {/* Mobile Title */}
      <div className="mobile-nav-title">
        ClimatePlans
      </div>

      {/* Mobile Hamburger Button */}
      <button 
        className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle navigation menu"
      >
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
      </button>

      {/* Navigation Items */}
      <div className={`nav-items ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        {navItems.map((item) => (
          <div
            key={item.section}
            className={`nav-item ${item.color} ${currentSection === item.section ? 'active' : ''}`}
            onClick={() => handleNavClick(item.section)}
          >
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
};
